/**
 * The wire between a game and its room (server/src/index.ts imports this file too: one source of truth).
 *
 * A ROOM is one logical place ("street:yaba", "place:restaurant:The Terrace, Victoria Island", "beach:…"). Everyone in
 * it sees everyone else: presence is automatic, interaction is intentional (a request one player sends and the other
 * answers on their own screen). The room only relays: each life's simulation stays on its own client.
 */
import type { RaceMail } from './raceproto';

/** where you are and what your body is doing, ~8 times a second */
export interface Pose {
  x: number; y: number; z: number;
  /** facing (radians, the player root's rotation.y) */
  yaw: number;
  /** 0 standing .. 1 walking; `run` 0..1 on top */
  move: number; run: number;
  /** a clip played in place ('dance', 'sit', 'phone'…), or null */
  emote: string | null;
  /** not drawn (in a car, an activity, a cutscene) */
  hidden?: boolean;
  /** your own car, parked on the road you're on (the others see it, and can ask you for a ride): model, colour, where.
   *  `v`: it's moving (you're at the wheel): only a player riding in it draws it (5 a second is too few to drive by) */
  car?: { m: string; c: number; x: number; z: number; yaw: number; v?: 1; /** your convoy behind you, in order: each car's model and colour */ f?: { m: string; c: number }[] };
}

export interface PeerInfo {
  id: string;
  name: string;
  /** the character's Appearance (human/model.ts), as saved */
  look: unknown;
  pose: Pose | null;
}

export type ClientMsg =
  | { t: 'hello'; id: string; name: string; look: unknown }
  | { t: 'pose'; pose: Pose }
  /** a message for one other player in the room (a request, an answer, a session step): relayed as-is */
  | { t: 'to'; id: string; data: unknown }
  /** the venue's stage (the DJ's mic, the bottle parade): a slot in the queue, played for everyone at once */
  | { t: 'stage'; kind: StageKind; name: string; over: string }
  /** something everyone here sees happen (money sprayed from your hand) */
  | { t: 'fx'; kind: 'spray'; at: [number, number, number]; dir: [number, number]; n: number };

/** what the venue's stage plays, and for how long (one at a time, in order) */
export type StageKind = 'shoutout' | 'bottle';
export const STAGE_MS: Record<StageKind, number> = { shoutout: 9_000, bottle: 26_000 };
/** a slot on the stage: who it's for, when it starts (server clock), and how many were ahead */
export interface StageEvent { id: number; kind: StageKind; by: string; name: string; over: string; at: number; ahead: number }

export type ServerMsg =
  /** `now`: the server's clock (games line their events up on it); `epoch`: when this room's night began (the music) */
  | { t: 'peers'; peers: PeerInfo[]; now: number; epoch: number }
  | { t: 'join'; peer: PeerInfo }
  | { t: 'leave'; id: string }
  | { t: 'pose'; id: string; pose: Pose }
  | { t: 'from'; id: string; data: unknown }
  | { t: 'stage'; ev: StageEvent; now: number }
  | { t: 'fx'; id: string; kind: 'spray'; at: [number, number, number]; dir: [number, number]; n: number }
  | { t: 'full' };

/** limits the room enforces (and the client respects) */
export const LIMITS = { name: 40, look: 12_000, data: 8_000, peers: 60 } as const;

// --- the inbox: one per player, across rooms (messages to people you've swapped numbers with) -------------------

/** a player's own car, as another player's game draws it when it comes to pick them up */
export interface LiftCar { name: string; model: string; colour: number; colourName: string }
/** where a player can be picked up from: their home (the house, drawn from this), a street (and where on it), a place */
export type PickupSpot = { home: { id: string; label: string; area: string; style: string } } | { at: string };

/** something one player sends another wherever they are: a text now; invites later */
export type InboxData =
  | { k: 'text'; text: string; /** its id, the same on both phones (what a reaction points at) */ mid?: string }
  /** a photo: the picture is in your inbox already (GET /vn/<you>/<id>), this says it's there */
  | { k: 'pic'; id: string }
  /** a reaction on one of the messages between you (an emoji; '' takes it off) */
  | { k: 'react'; mid: string; e: string }
  /** come on a trip with me: to this country, flying from this airport */
  | { k: 'trip'; rid: string; country: string; from: string; pickup?: LiftCar }
  | { k: 'trip-answer'; rid: string; yes: boolean; from?: PickupSpot }
  /**
   * A lift in a player's car: 'go' (I'm off to `dest`: your ride with me is at your kerb), 'here' (outside where you
   * are, to take you to `dest`), 'in' (I'm in your car), 'off' (not coming / not waiting). `dest`: a Destination, as JSON.
   */
  | { k: 'lift'; rid: string; step: 'go' | 'here' | 'in' | 'off'; dest?: string; label?: string; car?: LiftCar; home?: { id: string; label: string; area: string; style: string } }
  /** come and hang out: at this venue (spot and its name, in this region), now */
  | { k: 'hang'; rid: string; spot: string; name: string; region: string; home?: { id: string; label: string; area: string; style: string }; pickup?: LiftCar }
  /** (`from`: picked up from here, when the invite offered a pickup and they took it) */
  | { k: 'hang-answer'; rid: string; yes: boolean; from?: PickupSpot; /** already there (in that house): no trip */ here?: boolean }
  /** on one of their posts: a like (or taking it back), a comment */
  | { k: 'like'; post: string; on: boolean }
  | { k: 'comment'; post: string; text: string }
  /** a voice note: the audio is in your inbox already (GET /vn/<you>/<id>), this says it's there */
  | { k: 'vn'; id: string; secs: number }
  /** money, in the game's units (the sender's game took it out of their account) */
  | { k: 'money'; amount: number; rid: string; /** a large one, through the bank */ bank?: boolean; /** (from the Treasury) your salary for this office */ salary?: string }
  /** flowers or a toy won at the fair, sent to a player: it goes in their home */
  | { k: 'gift'; what: 'flowers' | 'plush'; kind: string; label: string; colour: number; note: string; rid: string }
  /** add me as a contact (you found my @username, or I found yours): the other says yes or no */
  | { k: 'contact'; handle: string }
  /** a message to someone who isn't your contact yet (a message request: two at most till they accept) */
  | { k: 'ask-text'; handle: string; text: string }
  | { k: 'contact-answer'; yes: boolean }
  /** "be my sister": `as` is what the sender would be to you (your life records them so: your sister's ask says Brother or Sister) */
  | { k: 'rel'; rid: string; as: string }
  | { k: 'rel-answer'; rid: string; yes: boolean; why?: string }
  /** that link's over (either of you, from Contacts) */
  | { k: 'rel-end' }
  /** down on one knee, a ring (RINGS' id), the asker's sex (so the other knows: fiancé or fiancée) */
  | { k: 'propose'; rid: string; ring: string; sex: 'm' | 'f' }
  | { k: 'propose-answer'; rid: string; yes: boolean; sex: 'm' | 'f' }
  /** the wedding, planned (and paid) by the sender: the same day and hall for you */
  | { k: 'wedding-plan'; wid?: string; date?: number; size?: string; venue?: string } // (wid: the wedding on the server, real time; date…: an old game's, on its own clock)
  /** a guest's invite to a wedding between players */
  | { k: 'wedding-guest'; wid?: string; rid?: string; date?: number; venue?: string; couple: string }
  | { k: 'wedding-rsvp'; wid: string; yes: boolean }
  | { k: 'wedding-cancel'; wid: string; couple: string }
  /** at the arch: "I do" (or walked out: off) */
  | { k: 'vow'; off?: boolean }
  /** the engagement called off (from Contacts) */
  | { k: 'wedding-off' }
  /** (married) come and live with me: the house, as it's done up; the country (the same one only) */
  | { k: 'movein'; rid: string; home: { id: string; label: string; area: string; style: string }; region: string }
  | { k: 'movein-answer'; rid: string; yes: boolean }
  | { k: 'baby-ask'; rid: string }
  | { k: 'baby-answer'; rid: string; yes: boolean; due?: number }
  /** living apart again (either of you) */
  | { k: 'moveout' }
  /** the owner's done the house up: the mover's copy */
  | { k: 'home-style'; home: { id: string; label: string; area: string; style: string } }
  | VisitMail
  | RaceMail;
/** what an inbox socket receives: queued and new deliveries (ack them once they're saved) */
export type InboxMsg = { t: 'mail'; seq: number; from: string; name: string; data: InboxData; at: number };

// --- lifts between players: ONE record, on the server, that both games draw from ----------------------------------

/**
 * A lift as the server holds it: whose car, who's in it, where it's going. The driver's inbox and the passenger's
 * keep the same record (the passenger's is their one seat: never two cars at once). Nobody is "in" until the server
 * says `boarded`; neither game shows a passenger in a car before that.
 *   offered → (the passenger gets in: the server checks the offer, the seat, the driver) → boarded → travelling → gone
 */
export interface LiftState {
  rid: string;
  driver: string; driverName: string;
  passenger: string; passengerName: string;
  phase: 'offered' | 'boarded' | 'travelling';
  car: LiftCar;
  /** when it last changed (server ms) */
  at: number;
  /** travelling: where to (a Destination as JSON, as the driver's game has it), in words, how long the trip is (game minutes) */
  dest?: string; label?: string; mins?: number;
  /** travelling to the driver's own house: it, to draw */
  home?: { id: string; label: string; area: string; style: string };
}
/** why a lift ended (each game says it in its own words) */
export type LiftWhy = 'declined' | 'cancelled' | 'out' | 'parked' | 'arrived' | 'gone' | 'expired' | 'failed' | 'replaced';
/** the inbox socket's other news (not mail: not queued, not acked): your lift as it is now (null: none) */
export type InboxPush = { t: 'lift'; lift: LiftState | null; rid?: string; why?: LiftWhy };
/** POST /lift: one step of a lift, proved with your own key. The server decides; the answer is the record (or why not) */
export type LiftBody = { id: string; key: string; name: string; rid: string } & (
  | { op: 'offer'; to: string; toName: string; car: LiftCar }
  | { op: 'board'; driver: string }
  | { op: 'go'; dest: string; label: string; mins: number; home?: { id: string; label: string; area: string; style: string } }
  | { op: 'off'; why: LiftWhy });
/** a step as a game sends it (its id, key and name go on in the inbox client) */
export type LiftStep = LiftBody extends infer B ? B extends unknown ? Omit<B, 'id' | 'key' | 'name'> : never : never;
export type LiftAnswer = { ok: true; lift: LiftState | null } | { ok: false; why: 'no-offer' | 'busy' | 'driver-offline' | 'seat-taken' | 'not-yours' | 'bad' };

// --- Visit: other players' homes, anywhere (a request each time; the owner says yes on their own screen) ------------

/** a home in the Visit app: whose, where (the owner's own game publishes it: the house they actually have) */
export interface HomeListing { id: string; name: string; handle: string; country: string; area: string; label: string; at: number; /** the house (its picture) */ homeId?: string; /** the region it's drawn in */ region?: string; online?: boolean }
/** someone asking to visit your home (the owner's list, from the server) */
export interface VisitAsk { id: string; name: string; at: number }
/** where a visit request stands, for the visitor */
export interface VisitStatus { host: string; status: 'pending' | 'accepted' | 'declined' | 'expired'; at: number; until?: number }
/** POST /visit */
export type VisitBody = { id: string; key: string; name: string; to: string } & ({ op: 'ask' } | { op: 'answer'; yes: boolean } | { op: 'cancel' } | { op: 'enter' });
/** a visit request (to the owner), the answer (to the visitor), taken back: server-made deliveries (no game can send one) */
export type VisitMail = { k: 'visit'; handle: string } | { k: 'visit-answer'; yes: boolean; country: string } | { k: 'visit-cancel' };
/** what an inbox socket sends: saved up to here (the inbox forgets them) */
/** (`gifts`: this game takes money from Support; a gift's ack says `gift`, the only thing that clears one) */
export type InboxAck = { t: 'ack'; seq: number; gift?: boolean } | { t: 'sync'; gifts?: boolean };
/** a post on a player's profile (what other players' feeds show; the picture is at /img/<player>/<id>) */
export interface PlayerPost { id: string; caption: string; place: string; likes: number; at: number; /** a reel: the clip is at GET /reel/<player>/<id> */ reel?: boolean }
/** POST /post: publish to your own profile */
export interface PostBody { from: string; key: string; post: PlayerPost; img: string; /** your name, for the global feed (For you) */ name?: string }
/** one post in the global feed (For you): whose, and the post */
export interface GlobalPost extends PlayerPost { from: string; name: string; handle: string }
/** one comment on a player's post, kept with the post (everyone who opens it sees the same thread) */
export interface ThreadComment { id: string; from: string; name: string; handle?: string; text: string; at: number; /** the comment it answers */ reply?: string }
/** a post's thread: the comments, and the players who liked it */
export interface Thread { comments: ThreadComment[]; likes: { from: string; name: string }[] }
/** POST /comment: on someone's post (proving who you are with your own key) */
export interface CommentBody { from: string; key: string; owner: string; post: string; name: string; handle?: string; text: string; reply?: string }
/** POST /like */
export interface LikeBody { from: string; key: string; owner: string; post: string; name: string; on: boolean }
/** POST /send */
export interface SendBody { from: string; key: string; to: string; name: string; data: InboxData }

/**
 * Money packs: real money (Paystack, in naira) for money in your life. The server reads the price and the credit from
 * here and from nowhere else (the game never says what a payment was worth). `credit` is Lagos-sized: a life elsewhere
 * gets what that sum is worth where they live (sim purchases.ts).
 */
export const PACKS = [
  { id: 'pocket', label: 'Pocket money', price: 1_500, credit: 300_000 },
  { id: 'week', label: 'A good week', price: 5_000, credit: 1_200_000 },
  { id: 'windfall', label: 'Windfall', price: 15_000, credit: 4_500_000 },
] as const;
export type PackId = (typeof PACKS)[number]['id'];
/** a paid pack, waiting on the account for the game to put it in the bank */
export interface Credit { ref: string; pack: PackId; credit: number; label: string; at: number }

